/** App shell: SidebarNav + TopBar + routed content (doc 01 §7.1).
 *
 * The navigation tree is exactly the one in the UI/UX document - Library
 * (Dataset / Model / Metrics Store), Evaluation (New Evaluation, Evaluation
 * Runs), Settings. Nothing added.
 */

import {
  Activity,
  BarChart3,
  Boxes,
  Brain,
  ChevronsLeft,
  ChevronsRight,
  Database,
  Moon,
  PanelsTopLeft,
  Play,
  Settings,
  Sun,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'

import { API_BASE } from '@/api/client'
import { useHealth, useMe, useRuns } from '@/api/queries'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'

const NAV = [
  {
    label: 'Library',
    icon: Boxes,
    items: [
      { to: '/library/datasets', label: 'Dataset Store', icon: Database },
      { to: '/library/models', label: 'Model Store', icon: Brain },
      { to: '/library/metrics', label: 'Metrics Store', icon: BarChart3 },
    ],
  },
  {
    label: 'Evaluation',
    icon: PanelsTopLeft,
    items: [
      { to: '/evaluations/new', label: 'New Evaluation', icon: Play },
      { to: '/evaluations', label: 'Evaluation Runs', icon: Activity },
    ],
  },
] as const

type Theme = 'light' | 'dark' | 'system'

function useTheme() {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('benchmark.theme') as Theme | null) ?? 'system',
  )
  useEffect(() => {
    if (theme === 'system') {
      delete document.documentElement.dataset.theme
      localStorage.removeItem('benchmark.theme')
    } else {
      document.documentElement.dataset.theme = theme
      localStorage.setItem('benchmark.theme', theme)
    }
  }, [theme])
  return { theme, setTheme }
}

function breadcrumbFor(pathname: string): string {
  if (pathname.startsWith('/library/datasets')) return 'Library / Dataset Store'
  if (pathname.startsWith('/library/models')) return 'Library / Model Store'
  if (pathname.startsWith('/library/metrics')) return 'Library / Metrics Store'
  if (pathname.startsWith('/evaluations/new')) return 'Evaluation / New Evaluation'
  if (pathname === '/evaluations') return 'Evaluation / Evaluation Runs'
  if (pathname.startsWith('/evaluations/')) return 'Evaluation / Evaluation Runs / Detail'
  if (pathname.startsWith('/settings')) return 'Settings'
  return 'Benchmark Platform'
}

export function AppShell() {
  const location = useLocation()
  const { theme, setTheme } = useTheme()
  const [collapsed, setCollapsed] = useState(false)
  const health = useHealth()
  const me = useMe()

  // Live badge with the count of in-flight evaluations.
  const activeRuns = useRuns({
    page: 1,
    size: 1,
    filters: { status: ['running', 'queued'] },
  })
  const activeCount = activeRuns.data?.total ?? 0
  const engineEnabled =
    (health.data?.checks?.execution_engine as { operational?: boolean } | undefined)
      ?.operational ?? true

  return (
    <div className="flex min-h-screen">
      <aside
        className="shrink-0 border-r border-[var(--border)] bg-[var(--surface-1)] transition-[width]"
        style={{
          width: collapsed ? 'var(--app-sidebar-w-collapsed)' : 'var(--app-sidebar-w)',
        }}
      >
        <div className="flex h-[var(--app-topbar-h)] items-center gap-2 px-4">
          <Boxes className="size-5 text-[var(--series-1)]" aria-hidden />
          {!collapsed && <span className="text-sm font-semibold">Benchmark Platform</span>}
        </div>
        <nav aria-label="Main" className="px-2 pb-4">
          {NAV.map((group) => (
            <div key={group.label} className="mb-3">
              {!collapsed && (
                <p className="px-2 py-1 text-[11px] font-semibold tracking-wide text-[var(--text-muted)] uppercase">
                  {group.label}
                </p>
              )}
              <ul>
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/evaluations'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-[13px]',
                          isActive
                            ? 'bg-[color-mix(in_oklab,var(--series-1)_12%,transparent)] font-medium text-[var(--series-1)]'
                            : 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
                        )
                      }
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {!collapsed && item.to === '/evaluations' && activeCount > 0 && (
                        <span className="ml-auto rounded-full bg-[var(--series-1)] px-1.5 text-[10px] font-semibold text-white">
                          {activeCount}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="mt-2 border-t border-[var(--border)] pt-2">
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-[13px]',
                  isActive
                    ? 'bg-[color-mix(in_oklab,var(--series-1)_12%,transparent)] font-medium text-[var(--series-1)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
                )
              }
            >
              <Settings className="size-4 shrink-0" aria-hidden />
              {!collapsed && <span>Settings</span>}
            </NavLink>
          </div>
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[var(--app-topbar-h)] items-center gap-3 border-b border-[var(--border)] bg-[var(--surface-1)] px-[var(--gutter)]">
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? (
              <ChevronsRight className="size-4" />
            ) : (
              <ChevronsLeft className="size-4" />
            )}
          </Button>
          <nav aria-label="Breadcrumb" className="text-[13px] text-[var(--text-secondary)]">
            {breadcrumbFor(location.pathname)}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {!engineEnabled && (
              <span
                className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{
                  background: 'color-mix(in oklab, var(--status-warning) 16%, transparent)',
                }}
                title="The Celery execution engine is not enabled in this deployment (Phase 2). Runs are persisted and queued but not executed."
              >
                Engine: Phase 2
              </span>
            )}
            {me.data && (
              <span className="max-w-[220px] truncate text-[12px] text-[var(--text-secondary)]">
                {me.data.display_name || me.data.subject}
                <span className="ml-1 text-[var(--text-muted)]">({me.data.role})</span>
              </span>
            )}
            {me.data?.auth_mode === 'sso' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  void fetch(`${API_BASE}/auth/logout`, {
                    method: 'POST',
                    credentials: 'include',
                  }).finally(() => {
                    window.location.assign('/login')
                  })
                }}
              >
                Log out
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              aria-label="Toggle theme"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
          </div>
        </header>

        <main className="flex-1 px-[var(--gutter)] py-6">
          <div className="mx-auto w-full max-w-[var(--content-max-w)]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
